import React, { useState, useEffect, useContext, useRef } from "react";
import { useHistory } from "react-router-dom";

import { i18n } from "../../translate/i18n";
import api from "../../services/api";
import toastError from "../../errors/toastError";
import useQueues from "../../hooks/useQueues";
import useWhatsApps from "../../hooks/useWhatsApps";
import { AuthContext } from "../../context/Auth/AuthContext";
import { Can } from "../Can";
import { Modal, Button, Input, Select } from "../ui";

// Icons
const SpinnerIcon = ({ className }) => (
  <svg className={`animate-spin ${className}`} viewBox="0 0 24 24" fill="none">
    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
    <path
      className="opacity-75"
      fill="currentColor"
      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
    />
  </svg>
);

const TransferTicketModal = ({ modalOpen, onClose, ticketid, ticketWhatsappId }) => {
  const history = useHistory();
  const [options, setOptions] = useState([]);
  const [queues, setQueues] = useState([]);
  const [allQueues, setAllQueues] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchParam, setSearchParam] = useState("");
  const [selectedUser, setSelectedUser] = useState(null);
  const [selectedQueue, setSelectedQueue] = useState("");
  const [selectedWhatsapp, setSelectedWhatsapp] = useState(ticketWhatsappId);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const dropdownRef = useRef(null);

  const { findAll: findAllQueues } = useQueues();
  const { loadingWhatsapps, whatsApps } = useWhatsApps();
  const { user: loggedInUser } = useContext(AuthContext);

  useEffect(() => {
    const loadQueues = async () => {
      const list = await findAllQueues();
      setAllQueues(list);
      setQueues(list);
    };
    loadQueues();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!modalOpen || searchParam.length < 3) {
      setLoading(false);
      return;
    }
    setLoading(true);
    const delayDebounceFn = setTimeout(() => {
      const fetchUsers = async () => {
        try {
          const { data } = await api.get("/users/", {
            params: { searchParam },
          });
          setOptions(data.users);
          setLoading(false);
        } catch (err) {
          setLoading(false);
          toastError(err);
        }
      };
      fetchUsers();
    }, 500);
    return () => clearTimeout(delayDebounceFn);
  }, [searchParam, modalOpen]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleClose = () => {
    onClose();
    setSearchParam("");
    setSelectedUser(null);
  };

  const handleSelectUser = (user) => {
    setSelectedUser(user);
    setSearchParam(user.name);
    setIsDropdownOpen(false);
    if (user && Array.isArray(user.queues)) {
      setQueues(user.queues);
    } else {
      setQueues(allQueues);
      setSelectedQueue("");
    }
  };

  const handleSaveTicket = async (e) => {
    e.preventDefault();
    if (!ticketid) return;
    setLoading(true);
    try {
      let data = {};

      if (selectedUser) {
        data.userId = selectedUser.id;
      }

      if (selectedQueue && selectedQueue !== null) {
        data.queueId = selectedQueue;

        if (!selectedUser) {
          data.status = "pending";
          data.userId = null;
        }
      }

      if (selectedWhatsapp) {
        data.whatsappId = selectedWhatsapp;
      }

      await api.put(`/tickets/${ticketid}`, data);

      setLoading(false);
      history.push(`/tickets`);
    } catch (err) {
      setLoading(false);
      toastError(err);
    }
  };

  return (
    <Modal
      open={modalOpen}
      onClose={handleClose}
      title={i18n.t("transferTicketModal.title")}
      maxWidth="md"
    >
      <form onSubmit={handleSaveTicket}>
        <div className="space-y-4">
          {/* User Autocomplete */}
          <div className="relative" ref={dropdownRef}>
            <label className="block text-sm font-medium text-dark-700 dark:text-dark-300 mb-1">
              {i18n.t("transferTicketModal.fieldLabel")}
            </label>
            <div className="relative">
              <Input
                value={searchParam}
                onChange={(e) => {
                  setSearchParam(e.target.value);
                  setSelectedUser(null);
                  setIsDropdownOpen(true);
                }}
                onFocus={() => setIsDropdownOpen(true)}
                placeholder={i18n.t("transferTicketModal.fieldLabel")}
                autoFocus
              />
              {loading && (
                <div className="absolute right-3 top-1/2 -translate-y-1/2">
                  <SpinnerIcon className="w-5 h-5 text-dark-400" />
                </div>
              )}
            </div>

            {/* Dropdown */}
            {isDropdownOpen && options.length > 0 && (
              <div className="absolute z-50 w-full mt-1 bg-white dark:bg-dark-800 border border-dark-200 dark:border-dark-700 rounded-lg shadow-lg max-h-60 overflow-y-auto">
                {options.map((user) => (
                  <button
                    key={user.id}
                    type="button"
                    onClick={() => handleSelectUser(user)}
                    className="w-full px-4 py-2 text-left text-sm text-dark-700 dark:text-dark-200 hover:bg-dark-100 dark:hover:bg-dark-700 transition-colors"
                  >
                    {user.name}
                  </button>
                ))}
              </div>
            )}

            {isDropdownOpen && searchParam.length >= 3 && !loading && options.length === 0 && (
              <div className="absolute z-50 w-full mt-1 bg-white dark:bg-dark-800 border border-dark-200 dark:border-dark-700 rounded-lg shadow-lg p-4 text-sm text-dark-500 dark:text-dark-400">
                {i18n.t("transferTicketModal.noOptions")}
              </div>
            )}
          </div>

          {/* Queue Select */}
          <div>
            <label className="block text-sm font-medium text-dark-700 dark:text-dark-300 mb-1">
              {i18n.t("transferTicketModal.fieldQueueLabel")}
            </label>
            <Select
              value={selectedQueue}
              onChange={(e) => setSelectedQueue(e.target.value)}
            >
              <option value="">&nbsp;</option>
              {queues.map((queue) => (
                <option key={queue.id} value={queue.id}>
                  {queue.name}
                </option>
              ))}
            </Select>
          </div>

          {/* WhatsApp Select */}
          <Can
            role={loggedInUser.profile}
            perform="ticket-options:transferWhatsapp"
            yes={() =>
              !loadingWhatsapps && (
                <div>
                  <label className="block text-sm font-medium text-dark-700 dark:text-dark-300 mb-1">
                    {i18n.t("transferTicketModal.fieldConnectionLabel")}
                  </label>
                  <Select
                    value={selectedWhatsapp}
                    onChange={(e) => setSelectedWhatsapp(e.target.value)}
                  >
                    {whatsApps.map((whatsapp) => (
                      <option key={whatsapp.id} value={whatsapp.id}>
                        {whatsapp.name}
                      </option>
                    ))}
                  </Select>
                </div>
              )
            }
          />
        </div>

        {/* Actions */}
        <div className="flex justify-end gap-2 mt-6 pt-4 border-t border-dark-200 dark:border-dark-700">
          <Button
            type="button"
            variant="outlined"
            color="secondary"
            onClick={handleClose}
            disabled={loading}
          >
            {i18n.t("transferTicketModal.buttons.cancel")}
          </Button>
          <Button type="submit" disabled={loading}>
            {loading && <SpinnerIcon className="w-4 h-4 mr-2" />}
            {i18n.t("transferTicketModal.buttons.ok")}
          </Button>
        </div>
      </form>
    </Modal>
  );
};

export default TransferTicketModal;
